<template>
  <div class="certificate-list">
    <div v-if="certificates.length" class="cert-container">
      <div
        v-for="cert in certificates"
        :key="cert.thumb"
        class="certificate"
        :class="{ 
          'selected': selectedThumb === cert.thumb,
          'invalid': !cert.isValid 
        }"
        @click="selectCertificate(cert)"
      >
        <div class="cert-content">
          <div class="cert-name">{{ getCertificateName(cert.cn) }}</div>
          <div class="cert-hint">Действует до: {{ formatDate(cert.validToDate) }}</div>
          <div class="cert-hint">
            ИНН: {{ getInn(cert.cn) }}, 
            {{ getOgrnType(cert.cn) }}: {{ getOgrn(cert.cn) }}
          </div>
        </div>
      </div>
    </div>
    
    <div v-else class="empty-certs">
      <div class="empty-image">
        <img width="200px" src="/images/icons/mascote/slon_facepalm.png" alt="No certificates" />
      </div>
      <div class="empty-message">Нет доступных сертификатов</div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

export interface Certificate {
  isValid: boolean;
  cn: string;
  thumb: string;
  validToDate: Date;
}

export default defineComponent({
  name: 'CertificateList',
  props: {
    certificates: {
      type: Array as PropType<Certificate[]>,
      required: true
    },
    selectedThumb: {
      type: String,
      default: ''
    }
  },
  emits: ['select'],
  methods: {
    selectCertificate(cert: Certificate) {
      this.$emit('select', cert);
    },
    
    getCertificateName(cn: string): string {
      const match = cn.match(/CN=(.+?),/);
      return match ? match[1] : 'Неизвестный сертификат';
    },
    
    getInn(cn: string): string {
      const match = cn.match(/ИНН(\sЮЛ)?=(?<inn>.+?)(,|$)/);
      return match?.groups?.inn || 'Не указан';
    },
    
    getOgrn(cn: string): string {
      let match = cn.match(/ОГРН=(.+?)(,|$)/);
      if (!match) {
        match = cn.match(/ОГРНИП=(.+?)(,|$)/);
      }
      return match ? match[1] : 'Не указан';
    },
    
    getOgrnType(cn: string): string {
      return cn.includes('ОГРНИП=') ? 'ОГРНИП' : 'ОГРН';
    },
    
    formatDate(date: Date): string {
      return date.toLocaleString('ru-RU');
    }
  }
});
</script>

<style scoped>
.certificate-list {
  width: 100%;
}

.cert-container {
  display: flex;
  flex-direction: column;
}

.certificate {
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: 4px solid transparent;
}

.certificate:hover {
  background-color: #f5f5f5;
  transform: translateX(4px);
}

.certificate.selected {
  background-color: #fbfbfb;
}

.certificate.invalid {
  color: #d32f2f;
  border-left-color: #d32f2f;
}

.cert-content {
  line-height: normal;
}

.cert-name {
  font-weight: 500;
  margin-bottom: 4px;
}

.cert-hint {
  font-size: 12px;
  color: #666;
  margin-bottom: 2px;
}

.empty-certs {
  text-align: center;
  padding: 20px;
}

.empty-image {
  margin-bottom: 16px;
}

.empty-message {
  color: #666;
  font-size: 14px;
}
</style>